import { useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import type OpenSeadragon from 'openseadragon';
import type { DigitizedPrimarySourcePage } from '../types';

const ZOOM_FACTOR = 1.5;

export default function useIIIFViewer({
	index,
	sources,
	canvasKeyHandler,
	canvasPressHandler,
}: useIIIFViewerProps) {
	const [isInitialized, setisInitialized] = useState<boolean>(false);
	const [isReady, setIsReady] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [hasError, setHasError] = useState<boolean>(false);
	const [canZoomIn, setCanZoomIn] = useState<boolean>(true);
	const [canZoomOut, setCanZoomOut] = useState<boolean>(true);
	const [overlayHandles, setOverlayHandles] = useState<
		OpenSeadragonOverlayHandle[]
	>([]);

	const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);
	const OpenSeadragonRef = useRef<OpenSeadragonFn>(null);
	const viewerRef = useRef<OpenSeadragon.Viewer>(null);
	const sourcesRef = useRef<DigitizedPrimarySourcePage[]>(sources);

	const handleError = (error?: Error | string) => {
		if (typeof error === 'string') {
			console.error('IIIF Viewer error:', error);
		}

		if (error instanceof Error) {
			console.error('IIIF Viewer error:', error.message);
		}

		setHasError(true);
		setIsLoading(false);
	};

	const showImageAtIndex = useCallback((newIndex: number) => {
		setHasError(false);
		setIsLoading(true);

		queueMicrotask(() => {
			// Always ensure that portals are unmounted before OpenSeadragon
			// removes their parent elements from the DOM.
			flushSync(() => {
				setOverlayHandles([]);
			});

			// We're safe to allow OpenSeadragon to remove the existing overlay handles
			// now that the portals are guaranteed to have unmounted.
			viewerRef.current.clearOverlays();
			viewerRef.current.world.removeAll();

			// 'tile-drawn' is the closest event we can hook into that
			// guarantees the image is at least partially drawn. There's
			// no "finished drawing" event, unfortunately.
			viewerRef.current.addOnceHandler('tile-drawn', () => {
				const item = viewerRef.current.world.getItemAt(0);
				const { x: imageWidth, y: imageHeight } = item.getContentSize();
				const newOverlayHandles = (
					sourcesRef.current[newIndex]?.interpretations ?? []
				).map(({ x: xPercent, y: yPercent, id }) => {
					const el = document.createElement('div');
					el.setAttribute('data-overlay-id', id);
					el.style.pointerEvents = 'auto';
					el.style.position = 'relative';

					const imageX = (xPercent / 100) * imageWidth;
					const imageY = (yPercent / 100) * imageHeight;
					const location = item.imageToViewportCoordinates(imageX, imageY);

					viewerRef.current.addOverlay({
						element: el,
						location,
						placement: OpenSeadragonRef.current.Placement.CENTER,
					});

					return { el, id };
				});

				viewerRef.current.viewport.goHome(true);
				setOverlayHandles(newOverlayHandles);
				setIsLoading(false);
			});
			viewerRef.current.addOnceHandler('add-item-failed', ({ message }) =>
				handleError(message),
			);
			viewerRef.current.addTiledImage({
				tileSource: sourcesRef.current[newIndex].imageUrl,
			});
		});
	}, []);

	const reload = useCallback(() => {
		if (!isReady) return;
		showImageAtIndex(index);
	}, [index, isReady, showImageAtIndex]);

	const zoomIn = useCallback(() => {
		if (!isReady) return;
		const viewport = viewerRef.current.viewport;
		viewport.zoomBy(ZOOM_FACTOR, null, false);
		viewport.applyConstraints();
	}, [isReady]);
	const zoomOut = useCallback(() => {
		if (!isReady) return;
		const viewport = viewerRef.current.viewport;
		viewport.zoomBy(1 / ZOOM_FACTOR, null, false);
		viewport.applyConstraints();
	}, [isReady]);
	const setMargins = useCallback(
		(margins: {
			top?: number;
			right?: number;
			bottom?: number;
			left?: number;
		}) => {
			if (!isReady) return;

			// Default to zero for each margin value if not specified.
			viewerRef.current.viewport.setMargins(margins);
			viewerRef.current.world.update(true);
		},
		[isReady],
	);

	useEffect(() => {
		// We need to wait until the consumer calls setContainerEl so we have a container in which to render.
		if (!containerEl) return;

		// We need to track this in case this component unmounts before the dynamic import completes.
		// This happens all the time in dev where we have React's StrictMode enabled. Yay for idempotency!
		let hasUnmounted = false;

		// All this rigamarole is required because OpenSeadragon has top-level references
		// to browser APIs and must be dynamically imported to avoid SSR issues. Another option
		// we have is to use `next/dynamic` to dynamically import this component, but I chose
		// this path because I wanted to:
		//
		// A) Avoid putting the responsibility on the parent of this component to remember to
		//    dynamically import it, as this could easily lead to accidentally introducing errors
		//    during SSR.
		// B) Keep both initial and subsequent page loading state logic contained within this
		//    component rather than splitting it between here and the parent.
		//
		// Issue to track for this: https://github.com/openseadragon/openseadragon/issues/2105

		// eslint-disable @typescript-eslint/no-floating-promises - Everything in this function is wrapped in a try/catch and doesn't rethrow so we won't have any unhandled promise rejections.
		(async () => {
			try {
				setIsLoading(true);

				if (!OpenSeadragonRef.current) {
					OpenSeadragonRef.current = await loadOpenSeadragon();
				}

				if (hasUnmounted) return;

				viewerRef.current = OpenSeadragonRef.current({
					tabIndex: -1,
					element: containerEl,
					crossOriginPolicy: 'Anonymous',
					defaultZoomLevel: 0,
					drawer: 'canvas',
					preserveImageSizeOnResize: false,
					autoResize: true,
					sequenceMode: false,
					gestureSettingsMouse: {
						clickToZoom: false,
						dblClickToZoom: false,
						dragToPan: true,
						scrollToZoom: true,
						pinchToZoom: true,
					},
					showHomeControl: false,
					showFlipControl: false,
					showFullPageControl: false,
					showNavigationControl: false,
					showNavigator: false,
					showRotationControl: false,
					showSequenceControl: false,
					showZoomControl: false,
				});

				// Note that the following event handlers don't need to be cleaned up
				// manually because Viewer.destroy() does that for us.

				// Wait until the first image is added (or fails to add) to mark the viewer as ready.
				viewerRef.current.world.addOnceHandler('add-item', () =>
					setIsReady(true),
				);
				viewerRef.current.addOnceHandler('add-item-failed', () =>
					setIsReady(true),
				);

				// These can fire from in-flight tile requests after the page
				// has changed. Ignore them if they're not for the active item.
				viewerRef.current.addHandler('tile-load-failed', (event) => {
					const eventItem = event.tiledImage;
					const activeItem = viewerRef.current.world.getItemAt(0);

					// Ignore tile load failures for non-active items.
					if (activeItem && eventItem && eventItem !== activeItem) {
						return;
					}

					// Ignore tiles that are not actively being drawn.
					if (!event.tile.beingDrawn) {
						return;
					}
					handleError(event.message);
				});
				viewerRef.current.addHandler('zoom', () => {
					const zoom = viewerRef.current.viewport.getZoom();
					setCanZoomIn(zoom < viewerRef.current.viewport.getMaxZoom());
					setCanZoomOut(zoom > viewerRef.current.viewport.getMinZoom());
				});
			} catch (err) {
				handleError(err);
			} finally {
				setisInitialized(true);
			}
		})();
		// eslint-enable @typescript-eslint/no-floating-promises

		return () => {
			hasUnmounted = true;

			queueMicrotask(() => {
				flushSync(() => {
					// Always ensure that portals are unmounted before OpenSeadragon
					// removes their parent elements from the DOM.
					setOverlayHandles([]);
				});

				if (viewerRef.current) {
					viewerRef.current.destroy();
					viewerRef.current = null;
				}
			});
		};
	}, [containerEl]);

	useEffect(() => {
		if (!isInitialized) return;
		showImageAtIndex(index);
	}, [showImageAtIndex, index, isInitialized]);

	// Bind these handlers in a seperate useEffect since we're passing them in as props.
	// This ensures we're avoiding stale closures and not rebinding the other stable handlers
	// every time these change.
	useEffect(() => {
		if (!isReady) return;
		const handleCanvasKey = (event: OpenSeadragon.CanvasKeyEvent) => {
			// Disable all default keybindings
			event.preventDefaultAction = true;
			canvasKeyHandler?.(event.originalEvent);
		};
		viewerRef.current.addHandler('canvas-key', handleCanvasKey);
		return () => {
			viewerRef.current.removeHandler('canvas-key', handleCanvasKey);
		};
	}, [isReady, canvasKeyHandler]);
	useEffect(() => {
		if (!isReady) return;
		const handleCanvasPress = ({
			originalEvent,
		}: OpenSeadragon.CanvasPressEvent) => {
			return canvasPressHandler?.(originalEvent);
		};
		viewerRef.current.addHandler('canvas-press', handleCanvasPress);
		return () => {
			viewerRef.current.removeHandler('canvas-press', handleCanvasPress);
		};
	}, [isReady, canvasPressHandler]);

	useEffect(() => {
		if (!isReady || !overlayHandles.length) return;
		overlayHandles.forEach(({ el }) => {
			el.style.visibility = isLoading ? 'hidden' : 'visible';
			el.style.pointerEvents = isLoading ? 'none' : 'auto';
		});
	}, [isReady, overlayHandles, isLoading]);

	return {
		setContainerEl,
		containerEl,
		isReady,
		hasError,
		isLoading,
		overlayHandles,
		canZoomIn,
		canZoomOut,
		zoomIn,
		zoomOut,
		setMargins,
		reload,
	};
}

async function loadOpenSeadragon(): Promise<OpenSeadragonFn> {
	try {
		const { default: OpenSeadragon } = await import('openseadragon');

		return OpenSeadragon;
	} catch (err) {
		throw new Error(`Failed to load OpenSeadragon.`, {
			cause: err,
		});
	}
}

interface useIIIFViewerProps {
	index: number;
	sources: DigitizedPrimarySourcePage[];
	canvasPressHandler?: EventListener;
	canvasKeyHandler?: EventListener;
}

type OpenSeadragonFn = typeof OpenSeadragon;

interface OpenSeadragonOverlayHandle {
	el: HTMLDivElement;
	id: string;
}
